import { compareAsc, format, setYear } from "date-fns";
import { convertInchesToTwip, Paragraph, ParagraphChild, TextRun } from "docx";
import { TFunction } from "next-i18next";

import { ExperienceProof } from "@/lib/application/proof/experienceProof";
import { experienceProofEmploymentI18n } from "@/lib/application/proof/experienceProofEmploymentI18n";
import { experienceProofEntrepreneurshipI18n } from "@/lib/application/proof/experienceProofEntrepreneurshipI18n";
import { experienceProofI18n } from "@/lib/application/proof/experienceProofI18n";

export const docStyles = {
  default: {
    title: {
      run: {
        size: 36,
        bold: true,
        color: "000000",
        font: "Arial",
      },
      paragraph: {
        spacing: {
          after: 120,
        },
      },
    },
    heading1: {
      run: {
        size: 36,
        bold: true,
        italics: false,
        color: "000000",
        font: "Arial",
      },
      paragraph: {
        spacing: {
          after: 120,
        },
      },
    },
    heading2: {
      run: {
        size: 28,
        bold: true,
        font: "Arial",
      },
      paragraph: {
        spacing: {
          before: 240,
          after: 120,
        },
      },
    },
    heading3: {
      run: {
        size: 22,
        bold: true,
        font: "Arial",
      },
      paragraph: {
        spacing: {
          before: 180,
          after: 120,
        },
      },
    },
    listParagraph: {
      run: {
        size: 22,
        font: "Arial",
      },
    },
  },
  paragraphStyles: [
    {
      id: "normal",
      name: "Normal",
      run: {
        size: 22,
        color: "#000000",
        font: "Arial",
      },
      paragraph: {
        spacing: {
          line: 300,
        },
      },
    },
    {
      id: "aside",
      name: "Aside",
      basedOn: "Normal",
      next: "Normal",
      run: {
        color: "999999",
        italics: true,
      },
      paragraph: {
        indent: {
          // left: convertInchesToTwip(0.5),
        },
        spacing: {
          line: 276,
        },
      },
    },
    {
      id: "wellSpaced",
      name: "Well Spaced",
      basedOn: "Normal",
      quickFormat: true,
      run: {
        size: 28,
      },
      paragraph: {
        spacing: {
          line: 276,
          before: 20 * 72 * 0.1,
          after: 20 * 72 * 0.05,
        },
      },
    },
    {
      id: "leftIndent1",
      basedOn: "normal",
      paragraph: {
        indent: {
          left: convertInchesToTwip(0.5),
        },
      },
    },
  ],
};

export const textRunMap = (input: string): TextRun[] => {
  const run: TextRun[] = [];

  const arr = input.split("\n");
  arr.map((a, i) => {
    const result = a.replaceAll("", " ");
    if (arr.length === 1 || i === 0) {
      run.push(new TextRun(`${result}`));
    } else {
      run.push(new TextRun({ text: `${result}`, break: 1 }));
    }
  });
  return run;
};

type SortableItem = {
  year?: string | null;

  fromYear?: string | null;
  toYear?: string | null;

  dateFrom?: string | null | Date;
  dateTo?: string | null | Date;

  workingNow?: boolean | null;
};

export function sortItemsByDate<T extends SortableItem>(items: T[]) {
  function pickData(x: T) {
    if (x.year) {
      return setYear(new Date(), parseInt(x.year));
    }

    if (x.fromYear || x.toYear) {
      return setYear(new Date(), parseInt((x.fromYear || x.toYear) as string));
    }

    if (x.dateFrom || x.dateTo) {
      return new Date((x.fromYear || x.toYear) as string);
    }

    if (x.workingNow) {
      return new Date();
    }

    return setYear(new Date(), 2100);
  }

  return items.sort((a: T, b: T) => {
    return compareAsc(pickData(a), pickData(b));
  });
}

export function renderDateRange(
  dateFrom?: string | Date | null,
  dateTo?: string | Date | null,
  workingNow?: boolean | null
) {
  return [
    dateFrom &&
      new TextRun({
        italics: true,
        text: format(new Date(dateFrom), "d MMM yyyy"),
      }),
    dateFrom &&
      (dateTo || workingNow) &&
      new TextRun({
        italics: true,
        text: " — ",
      }),
    dateTo &&
      !workingNow &&
      new TextRun({
        italics: true,
        text: format(new Date(dateTo), "d MMM yyyy"),
      }),
    workingNow &&
      new TextRun({
        italics: true,
        text: "now",
      }),
  ].filter(Boolean) as ParagraphChild[];
}

export function renderProofList(
  t: TFunction,
  proofs: string | null | undefined,
  instance: number
) {
  const list = (proofs?.split(",") ?? []).filter(Boolean) as ExperienceProof[];

  return list.map((proof) => {
    const titleKey = {
      ...experienceProofI18n,
      ...experienceProofEntrepreneurshipI18n,
      ...experienceProofEmploymentI18n,
    }[proof]?.title;

    return new Paragraph({
      numbering: {
        reference: "numbering",
        level: 0,
        instance,
        custom: true,
      },
      text: (titleKey && t(titleKey)) || "-",
    });
  });
}
